import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import Header from "../ReusedComponents/Header";
import { DecryptED } from "../../DecryptJS/Decrypt";
import { SendNotification } from "../ReusedComponents/SendNotification";
import { filterGenericStaff } from "../ReusedComponents/genericAccounts";
import { saveStyledWorkbook } from "../ReusedComponents/excelStyle";
import { printWithFileName } from "../ReusedComponents/printDocument";

export default function TicketReport({ props, parentId, setID, TicketData }) {
  const history = useHistory();
  const { sid, uid } = useParams();
  const isAdminRoute = history.location.pathname.indexOf("/tickets/admin/") === 0;
  const decUid = (function() { try { return DecryptED(uid); } catch (e) { return ""; } })();

  const [data, setData] = useState([]);
  //const [userName, setUserName] = useState("");
  const [userName, setUserName] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [staffDetails, setStaffDetails] = useState({});
  const [assignUpdating, setAssignUpdating] = useState(null);
  // An admin-route visitor who has no matching staff record at this school
  // (e.g. a generic/shared login like "SCHOOL ADMINISTRATOR" with no
  // committee/type assignment) gets create-ticket-only access instead of
  // full Admin ticket-management, without hardcoding any specific name.
  const [isRestrictedAdmin, setIsRestrictedAdmin] = useState(false);

  // Right panel
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState("Open");
  // "Assigned to me" / "Created by me" / "Both" — only meaningful on a user's
  // own report, where the list holds exactly those two kinds of ticket.
  const [filterRelation, setFilterRelation] = useState("All");
  const [filterSchool, setFilterSchool] = useState("All");
  const [filterCommittee, setFilterCommittee] = useState("All");
  const [filterAssignee, setFilterAssignee] = useState("All");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // Schools whose report board shows ALL tickets to every user (not just
  // their own). SCOPED LIST — every other school keeps the original
  // user-wise behaviour untouched.
  const SHOW_ALL_TICKETS_SIDS = ["1031"]; // "Evolve High School"

  function fetchTickets() {
    var showAllSid = false;
    try {
      showAllSid = SHOW_ALL_TICKETS_SIDS.indexOf(String(DecryptED(sid))) !== -1;
    } catch (e) {
      showAllSid = false;
    }

    function finish(tickets, effectiveShowAll) {
      if (parentId) {
        // Child tickets view — only show tickets linked to this parent
        var childTickets = tickets.filter(function(t) {
          return String(t.parent_id) === String(parentId);
        });
        setData(childTickets);
      } else if (isAdminRoute || effectiveShowAll) {
        // Any admin-route visitor sees every ticket, restricted or not —
        // edit/reassign rights for a restricted admin are gated per-ticket
        // (only tickets they created) separately, not by hiding rows here.
        setData(tickets);
      } else {
        // A regular user's report covers tickets assigned to them AND tickets
        // they created (the ones they raised were previously missing).
        var userTickets = tickets.filter(function(t) {
          return String(t.staff_id) === String(decUid) || String(t.uid) === String(decUid);
        });
        setData(userTickets);
      }
    }

    axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "GetTicketCreate",
      sid: sid,
      all: 1,
    }).then((res) => {
      var tickets = res.data && res.data.Tickets ? res.data.Tickets : [];
      if (showAllSid && !isAdminRoute) {
        // The full-board exception for this school is meant for the Head of
        // School only — not every staff member. Confirm the viewer's own
        // staff record actually holds that role before granting it.
        axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
          fun_name: "GetStaffDetails",
          sid: sid,
        }, { timeout: 20000 }).then((sres) => {
          var staffList = sres.data && sres.data.Staff_Details ? sres.data.Staff_Details : [];
          var decUid;
          try { decUid = DecryptED(uid); } catch (e) { decUid = ""; }
          var me = staffList.find(function(s) { return String(s.staff_id) === String(decUid); });
          var isHeadOfSchool = !!(me && String(me.type).trim().toUpperCase() === "HEAD OF SCHOOL");
          finish(tickets, isHeadOfSchool);
        }).catch(function() {
          finish(tickets, false);
        });
      } else {
        finish(tickets, showAllSid);
      }
    }).catch(function(err) {
      console.error('[TicketReport] fetch failed:', err);
      setData([]);
    });
  }

  function fetchStaffDetails() {
    axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "GetStaffDetails",
      sid: sid,
    }, { timeout: 20000 }).then((res) => {
      var a = [];
      var staffList = res.data && res.data.Staff_Details ? res.data.Staff_Details : [];
      staffList.forEach((i) => {
        if (a.length > 0 ? a[0][i.type] === undefined : true) {
          a.length === 0 ? a.push({ [i.type]: [i] }) : (a[0][i.type] = [i]);
        } else {
          a[0][i.type].push(i);
        }
      });
      setStaffDetails(a.length > 0 ? a[0] : {});

      var hasStaffRecord = staffList.some(function(s) { return String(s.staff_id) === String(decUid); });
      setIsRestrictedAdmin(!hasStaffRecord);
      fetchTickets();
    }).catch(() => {
      setStaffDetails({});
      // Fail open on a lookup error — don't lock out an existing Admin
      // because a network hiccup prevented confirming their staff record.
      setIsRestrictedAdmin(false);
      fetchTickets();
    });
  }

  function assignTicket(ticket, staffObj) {
    var newStaffName = staffObj ? staffObj.first_name : "";
    var newStaffId = staffObj ? staffObj.staff_id : "";
    if (String(ticket.staff_id || "") === String(newStaffId || "")) return;
    setAssignUpdating(ticket.id);
    var d = { nooffiles: 0, ...ticket, staff: newStaffName, staff_id: newStaffId };
    delete d.duedate1;
    const fm = new FormData();
    Object.keys(d).forEach((k) => {
      fm.append(k === "school" ? "schoolname" : k, d[k] == null ? "" : d[k]);
    });
    fm.append("fun_name", "Update_Ticket_Create");
    fm.append("sid", sid);
    const insertHistory = axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", {
      fun_name: "TicketHistoryInsert",
      sid: sid,
      ticket_id: ticket.id,
      date: moment().format("YYYY-MM-DD HH:mm:ss"),
      committee: ticket.committee,
      remark_reply: "",
      reply: 0,
      staff: userName || "Admin",
      status: ticket.status,
      history: `${userName || "Admin"} reassigned ticket to ${newStaffName || "Unassigned"}`,
      history_details: "",
      uid: uid,
      is_sadmin: 0,
    });
    const updateTicketReq = axios.post("https://topschool.prisms.in/rest/index.php/user_list.json?", fm);
    axios.all([updateTicketReq, insertHistory]).then(() => {
      if (newStaffId) {
        SendNotification({
          sid: sid,
          userid: newStaffId,
          ticket_id: ticket.id,
          action: `${userName || "Admin"} assigned a ticket to you`,
        });
      }
      fetchTickets();
    }).catch((err) => {
      console.error('[TicketReport] assign failed:', err);
    }).finally(() => {
      setAssignUpdating(null);
    });
  }

  useEffect(() => {
    if (isAdminRoute) {
      // Determines staff-record membership first, then fetches tickets with
      // the correct scope — avoids fetching tickets twice on admin routes.
      fetchStaffDetails();
    } else {
      fetchTickets();
    }

    axios({
      url: "https://topschool.prisms.in/rest/index.php/user_list.json?",
      method: "POST",
      data: {
        fun_name: "AUserInfo",   // ✅ REQUIRED
        sid: DecryptED(sid),
        u_id: DecryptED(uid),   // ⚠️ must be u_id (not uid)
      },
    })
      .then((res) => {
        console.log("USER API 👉", res.data);

        const d = res.data || {};

        let name = "";

        if (d.student && d.student.length > 0) {
          name = d.student[0].name;
        } else if (d.name) {
          name = d.name;
        } else {
          name = "User";
        }

        setUserName(name);
      })
      .catch((err) => {
        console.log("ERROR ❌", err);
        setUserName("User");
      });
  }, [sid, uid, parentId]);

  const open = data.filter((d) => d.status === "Open");
  //const progress = data.filter((d) => d.status === "In Progress");
  const closed = data.filter((d) => d.status === "Closed");

  const schoolNames = [
    "All",
    ...new Set(
      data
        .map((d) => d.school_name || d.schoolName || d.school || "Unknown")
        .filter(Boolean)
    ),
  ];

  const committeeNames = [
    "All",
    ...new Set(data.map((d) => d.committee || "Unassigned").filter(Boolean)),
  ];

  const assigneeNames = [
    "All",
    ...new Set(
      data.map((d) => d.staff || d.assigned_to || "Unassigned").filter(Boolean)
    ),
  ];

  // The personal report mixes two kinds of ticket, so each row says which it
  // is. Admin/child-ticket views list everyone's tickets, where this means
  // nothing — the column and its filter are hidden there.
  const personalScope = !isAdminRoute && !parentId;
  const relationOf = (d) => {
    var assignedToMe = String(d.staff_id) === String(decUid);
    var createdByMe = String(d.uid) === String(decUid);
    if (assignedToMe && createdByMe) return "Both";
    if (assignedToMe) return "Assigned to me";
    if (createdByMe) return "Created by me";
    return "—";
  };

  const filteredData = data.filter((d) => {
    const relationMatch =
      !personalScope ||
      filterRelation === "All" ||
      relationOf(d) === filterRelation ||
      relationOf(d) === "Both";
    if (!relationMatch) return false;
    const statusMatch = filterStatus === "All" || d.status === filterStatus;
    const schoolVal = d.school_name || d.schoolName || d.school || "Unknown";
    const schoolMatch = filterSchool === "All" || schoolVal === filterSchool;
    const committeeVal = d.committee || "Unassigned";
    const committeeMatch = filterCommittee === "All" || committeeVal === filterCommittee;
    const assigneeVal = d.staff || d.assigned_to || "Unassigned";
    const assigneeMatch = filterAssignee === "All" || assigneeVal === filterAssignee;
    var dateMatch = true;
    const ticketDate = d.created_at || d.createdAt || d.date || "";
    if (filterDateFrom && ticketDate)
      dateMatch = dateMatch && new Date(ticketDate) >= new Date(filterDateFrom);
    if (filterDateTo && ticketDate)
      dateMatch = dateMatch && new Date(ticketDate) <= new Date(filterDateTo + "T23:59:59");
    var searchMatch = true;
    if (searchQuery.trim() !== "") {
      const q = searchQuery.trim().toLowerCase();
      searchMatch =
        String(d.id || "").toLowerCase().indexOf(q) !== -1 ||
        (d.title || "").toLowerCase().indexOf(q) !== -1 ||
        assigneeVal.toLowerCase().indexOf(q) !== -1;
    }
    return statusMatch && schoolMatch && committeeMatch && assigneeMatch && dateMatch && searchMatch;
  });

  function sortTickets(arr, field, dir) {
    var withVal = arr.map(function (d) {
      var v;
      if (field === "id") v = Number(d.id) || 0;
      else if (field === "date") v = new Date(d.created_at || d.createdAt || d.date || 0).getTime();
      else if (field === "assigned") v = (d.staff || d.assigned_to || "Unassigned").toLowerCase();
      else v = (d[field] || "").toString().toLowerCase();
      return { d: d, v: v };
    });
    withVal.sort(function (a, b) {
      if (a.v < b.v) return dir === "asc" ? -1 : 1;
      if (a.v > b.v) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return withVal.map(function (x) { return x.d; });
  }

  const sortedData = sortField ? sortTickets(filteredData, sortField, sortDir) : filteredData;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortIndicator = (field) => (sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  const resetFilters = () => {
    setFilterStatus("All");
    setFilterRelation("All");
    setFilterSchool("All");
    setFilterCommittee("All");
    setFilterAssignee("All");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSearchQuery("");
    setSortField(null);
    setSortDir("asc");
  };

  const statusColor = (s) => {
    if (s === "Open") return "#2563eb";
    //if (s === "In Progress") return "#d97706";
    if (s === "Closed") return "#16a34a";
    return "#6b7280";
  };

  const priorityColor = (p) => {
    if (!p) return "#6b7280";
    const v = p.toLowerCase();
    if (v === "high") return "#dc2626";
    if (v === "medium") return "#d97706";
    if (v === "low") return "#16a34a";
    return "#6b7280";
  };

  // Any admin-route visitor (including a restricted admin with no matching
  // staff record) can reassign every ticket, same as a full Admin.
  const canManageTicket = () => isAdminRoute;

  const detailUrl = (ticket) => {
    var id = ticket && typeof ticket === "object" ? ticket.id : ticket;
    // Always stay under /tickets/admin/... for any admin-route visitor —
    // read-only enforcement for tickets a restricted admin doesn't own
    // happens inside the detail page itself, not via which URL is used.
    return (isAdminRoute ? "/tickets/admin/details/" : "/tickets/user/details/") + sid + "/" + uid + "/" + id;
  };

  const handleCardClick = (d) => {
    if (selectedTicket && selectedTicket.id === d.id) {
      history.push(detailUrl(d));
    } else {
      setSelectedTicket(d);
    }
  };

  const closePanel = () => setSelectedTicket(null);

  const exportToExcel = () => {
    var fmt = function(raw) {
      if (!raw) return "";
      var m = moment(raw, "YYYY-MM-DD HH:mm:ss");
      return m.isValid() ? m.format("DD MMM YYYY") : "";
    };

    var headers = [
      "Ticket ID", "Title", "Description", "Status", "Priority",
      "School", "Committee", "Raised By", "Assigned To",
      "Remark", "Date", "Due Date", "Updated On", "Closed By"
    ];
    // Same "Assigned to me / Created by me" column the personal report shows.
    if (personalScope) headers.splice(1, 0, "Type");

    var rows = sortedData.map(function(d) {
      if (personalScope) {
        return [
          "#" + d.id,
          relationOf(d),
          d.title || "",
          d.description || "",
          d.status || "",
          d.priority || "",
          d.subschool || d.school_name || d.schoolName || d.school || "",
          d.committee || "",
          d.name || d.creator || d.created_by || "",
          d.staff || d.assigned_to || "Unassigned",
          d.remark || "",
          fmt(d.created_at || d.createdAt || d.date),
          fmt(d.duedate),
          fmt(d.updated_on),
          d.closed_by || ""
        ];
      }
      return [
        "#" + d.id,
        d.title || "",
        d.description || "",
        d.status || "",
        d.priority || "",
        d.subschool || d.school_name || d.schoolName || d.school || "",
        d.committee || "",
        d.name || d.creator || d.created_by || "",
        d.staff || d.assigned_to || "Unassigned",
        d.remark || "",
        fmt(d.created_at || d.createdAt || d.date),
        fmt(d.duedate),
        fmt(d.updated_on),
        d.closed_by || ""
      ];
    });

    // Build a real .xlsx workbook. The old HTML-table ".xls" opened blank or
    // was blocked by newer Excel versions even though the rows were there.
    var ws = XLSX.utils.aoa_to_sheet([headers].concat(rows));
    // Free-text columns wrap and stay left-aligned; everything else is centred.
    var wrapHeaders = ["Title", "Description", "Remark", "School", "Raised By"];
    var columnStyles = headers.map(function(h) {
      return wrapHeaders.indexOf(h) !== -1 ? "wrap" : "center";
    });
    // Narrower caps for wrapped columns — the text wraps instead of running on.
    ws["!cols"] = headers.map(function(h, ci) {
      var longest = rows.reduce(function(m, r) { return Math.max(m, String(r[ci]).length); }, h.length);
      var cap = columnStyles[ci] === "wrap" ? 38 : 16;
      return { wch: Math.min(Math.max(longest + 2, 10), cap) };
    });
    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: headers.length - 1 } }),
    };
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");
    saveStyledWorkbook(wb, "Ticket_Report_" + moment().format("YYYY-MM-DD") + ".xlsx", {
      Tickets: columnStyles,
    });
  };

  const formatDate = (raw) => {
    if (!raw) return "—";
    return new Date(raw).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }

        .welcome-bar {
          background: #1e3a5f;
          color: #fff;
          padding: 10px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
          font-family: 'Segoe UI', sans-serif;
        }
        .welcome-text { font-weight: 600; letter-spacing: 0.3px; }
        .welcome-name { color: #7dd3fc; margin-left: 4px; }

        .view-toggle-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 20px;
          background: #fff;
          border-bottom: 1px solid #dde1e7;
          font-family: 'Segoe UI', sans-serif;
        }
        .toggle-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 16px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
          background: #fff;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          transition: all 0.15s;
        }
        .toggle-btn.active { background: #1e3a5f; color: #fff; border-color: #1e3a5f; }
        .toggle-btn:hover:not(.active) { background: #f3f4f6; }

        /* Main area: kanban + side panel side by side */
        .main-area {
          display: flex;
          font-family: 'Segoe UI', sans-serif;
          background: #f4f5f7;
          min-height: calc(100vh - 150px);
        }
        .board-area { flex: 1; min-width: 0; overflow: hidden; }

        /* Kanban */
        .kanban-board {
          display: flex;
          gap: 16px;
          padding: 16px;
          overflow-x: auto;
          min-height: calc(100vh - 150px);
        }
        .kanban-column {
          flex: 1;
          min-width: 240px;
          background: #ebecf0;
          border-radius: 10px;
          padding: 10px;
        }
        .kanban-col-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
        }
        .kanban-col-header h3 {
          font-size: 12px;
          font-weight: 700;
          margin: 0;
          color: #1e293b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .col-count {
          background: #c1c7d0;
          color: #42526e;
          border-radius: 10px;
          padding: 1px 7px;
          font-size: 11px;
          font-weight: 700;
        }
        .kanban-card {
          background: #fff;
          padding: 11px 12px;
          margin-bottom: 8px;
          border-radius: 8px;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          border-left: 4px solid transparent;
          transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
        }
        .kanban-card.selected { background: #eff6ff; box-shadow: 0 2px 8px rgba(37,99,235,0.18); }
        .kanban-card:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(0,0,0,0.13); }
        .kanban-card strong { display: block; font-size: 13px; color: #1e293b; margin-bottom: 4px; }
        .kanban-card p { font-size: 11px; color: #6b7280; margin: 0 0 6px 0; line-height: 1.4; }
        .card-footer { display: flex; justify-content: space-between; margin-top: 6px; font-size: 11px; }
        .card-id { color: #94a3b8; }
        .click-hint { font-size: 10px; color: #2563eb; margin-top: 5px; font-style: italic; }

        /* Right Detail Panel */
        .detail-panel {
          width: 340px;
          min-width: 300px;
          background: #fff;
          border-left: 1px solid #e2e8f0;
          box-shadow: -4px 0 20px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          max-height: calc(100vh - 150px);
          position: sticky;
          top: 0;
          animation: slideIn 0.22s ease;
        }
        @keyframes slideIn {
          from { transform: translateX(30px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 14px 14px 10px;
          border-bottom: 1px solid #f1f5f9;
          background: #f8fafc;
        }
        .panel-header-left { flex: 1; min-width: 0; }
        .panel-ticket-id { font-size: 11px; color: #94a3b8; font-weight: 600; margin-bottom: 5px; }
        .panel-title-link {
          font-size: 14px;
          font-weight: 700;
          color: #1e3a5f;
          text-decoration: none;
          line-height: 1.4;
          display: block;
          cursor: pointer;
          border-bottom: 1px dashed #93c5fd;
          padding-bottom: 2px;
          transition: color 0.15s;
          word-break: break-word;
        }
        .panel-title-link:hover { color: #2563eb; text-decoration: none; }
        .panel-close {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 17px;
          color: #94a3b8;
          padding: 0 2px;
          line-height: 1;
          transition: color 0.15s;
          flex-shrink: 0;
          margin-left: 8px;
        }
        .panel-close:hover { color: #374151; }
        .panel-body { padding: 14px; flex: 1; }
        .panel-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 11px;
        }
        .panel-label { color: #94a3b8; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .panel-value { color: #1e293b; font-size: 13px; font-weight: 500; text-align: right; }
        .panel-divider { border: none; border-top: 1px solid #f1f5f9; margin: 10px 0; }
        .panel-desc-label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
        .panel-desc { font-size: 12px; color: #374151; line-height: 1.6; background: #f8fafc; border-radius: 6px; padding: 9px; }
        .panel-open-btn {
          display: block;
          margin: 14px;
          padding: 9px;
          background: #1e3a5f;
          color: #fff;
          text-align: center;
          border-radius: 7px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s;
        }
        .panel-open-btn:hover { background: #2563eb; }

        /* Badges */
        .status-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          color: #fff;
          white-space: nowrap;
        }
        .priority-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          color: #fff;
          white-space: nowrap;
        }

        /* Filter Bar */
        .filter-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
          padding: 10px 20px;
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          font-family: 'Segoe UI', sans-serif;
        }
        .filter-bar label { font-size: 12px; font-weight: 600; color: #6b7280; margin-right: 4px; }
        .filter-bar select, .filter-bar input[type="date"], .filter-bar input[type="text"] {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 13px;
          color: #374151;
          background: #f9fafb;
          outline: none;
        }
        .filter-group { display: flex; align-items: center; gap: 5px; }
        .reset-btn {
          margin-left: auto;
          padding: 6px 14px;
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fca5a5;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }
        .reset-btn:hover { background: #fecaca; }
        .export-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          background: #16a34a;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s;
          margin-left: 8px;
        }
        .export-btn:hover { background: #15803d; }
        .print-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          background: #1e3a5f;
          color: #fff;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }
        .print-btn:hover { background: #274a73; }

        /* Shown only on paper */
        .print-header { display: none; }

        /* Assigned/Created tag on a user's own report */
        .rel-tag {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }
        .rel-assigned { background: #dbeafe; color: #1e40af; }
        .rel-created { background: #fef3c7; color: #92400e; }
        .rel-both { background: #dcfce7; color: #166534; }

        /* List Table */
        .list-view-wrap { padding: 16px; overflow-x: auto; font-family: 'Segoe UI', sans-serif; background: #f4f5f7; min-height: calc(100vh - 150px); }
        .results-count { font-size: 13px; color: #6b7280; margin-bottom: 10px; }
        .ticket-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
        .ticket-table thead tr { background: #1e3a5f; color: #fff; }
        .ticket-table th.sortable { cursor: pointer; user-select: none; }
        .ticket-table th.sortable:hover { background: #274a73; }
        .ticket-table th { padding: 11px 14px; text-align: left; font-size: 12px; font-weight: 600; letter-spacing: 0.5px; white-space: nowrap; }
        .ticket-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.1s; }
        .ticket-table tbody tr:hover { background: #f0f7ff; }
        .ticket-table td { padding: 10px 14px; font-size: 13px; color: #374151; vertical-align: middle; }
        .ticket-title-link {
          color: #1e3a5f;
          font-weight: 600;
          cursor: pointer;
          border-bottom: 1px dashed #93c5fd;
          text-decoration: none;
        }
        .ticket-title-link:hover { color: #2563eb; }
        .empty-state { text-align: center; padding: 50px 20px; color: #9ca3af; font-size: 14px; }

        /* ===== MOBILE RESPONSIVE ===== */

        @media only screen and (max-width: 768px) {
          .welcome-bar {
            padding: 8px 12px;
            font-size: 12px;
            flex-wrap: wrap;
            gap: 4px;
          }
          .welcome-text { font-size: 13px; }
          .welcome-name { display: block; font-size: 13px; margin-left: 0; }

          .view-toggle-bar {
            padding: 8px 12px;
            gap: 8px;
          }
          .toggle-btn {
            padding: 6px 12px;
            font-size: 12px;
            flex: 1;
            justify-content: center;
          }

          .filter-bar {
            padding: 8px 12px;
            gap: 6px;
          }
          .filter-bar label { font-size: 11px; }
          .filter-bar select, .filter-bar input[type="date"], .filter-bar input[type="text"] {
            padding: 6px 8px;
            font-size: 12px;
            flex: 1;
            min-width: 0;
          }
          .filter-group { flex: 1; min-width: 0; }
          .export-btn {
            padding: 6px 12px;
            font-size: 12px;
            margin-left: 0;
            width: 100%;
            justify-content: center;
          }
          .reset-btn {
            margin-left: 0;
            width: 100%;
            text-align: center;
          }
        }

        @media only screen and (max-width: 480px) {
          /* Kanban: stack columns vertically */
          .kanban-board {
            flex-direction: column;
            gap: 12px;
            padding: 10px;
            min-height: auto;
            overflow-x: hidden;
          }
          .kanban-column {
            min-width: unset;
            width: 100%;
            flex: unset;
          }
          .kanban-card {
            padding: 10px;
          }
          .kanban-card strong { font-size: 13px; }
          .kanban-card p { font-size: 11px; }
          .card-footer { font-size: 11px; }

          /* Main area: single column */
          .main-area {
            flex-direction: column;
            min-height: auto;
          }
          .board-area {
            width: 100%;
            overflow-x: hidden;
          }

          /* Detail panel: full width overlay */
          .detail-panel {
            width: 100% !important;
            min-width: unset !important;
            max-height: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 100;
            border-left: none;
            border-radius: 0;
          }
          .panel-header { padding: 12px; }
          .panel-body { padding: 12px; }
          .panel-open-btn { margin: 12px; }

          /* List Table: horizontal scroll */
          .list-view-wrap {
            padding: 10px;
          }
          .ticket-table {
            font-size: 12px;
            display: block;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .ticket-table th { padding: 8px 10px; font-size: 11px; }
          .ticket-table td { padding: 8px 10px; font-size: 12px; }

          /* Welcome bar */
          .welcome-bar {
            padding: 6px 10px;
            font-size: 11px;
          }

          /* Filter bar: stack filters */
          .filter-bar {
            flex-direction: row;
            flex-wrap: wrap;
            padding: 6px 10px;
            gap: 5px;
          }
          .filter-bar select, .filter-bar input[type="date"], .filter-bar input[type="text"] {
            flex: 1 1 45%;
            min-width: 0;
          }
          .filter-group {
            flex: 1 1 45%;
          }

          /* Toggle buttons */
          .view-toggle-bar {
            padding: 6px 10px;
            gap: 6px;
          }
          .toggle-btn {
            padding: 6px 10px;
            font-size: 11px;
          }
        }

        /* ── Print layout: just the report table, sized to fit the page ── */
        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          body { background: #fff !important; }
          /* Sticky/animated elements make print-to-PDF drivers produce broken
             or empty files, so everything is static and still on paper. */
          * { animation: none !important; transition: none !important; }
          .detail-panel, .welcome-bar, .view-toggle-bar, .filter-bar { position: static !important; }
          /* Description is the widest column and its text is already cut to
             100 characters on screen; dropping it keeps the rest readable,
             even if the print dialog is switched to Portrait. */
          .col-desc { display: none !important; }
          .welcome-bar, .view-toggle-bar, .filter-bar, .detail-panel,
          .TMHeader, .results-count, .print-btn, .export-btn, .reset-btn,
          .click-hint, .panel-open-btn { display: none !important; }
          .main-area, .board-area, .list-view-wrap {
            display: block !important;
            min-height: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            background: #fff !important;
          }
          .print-header { display: block !important; margin-bottom: 8px; font-family: 'Segoe UI', sans-serif; }
          .print-header h2 { margin: 0 0 2px 0; font-size: 15pt; color: #0b2545; }
          .print-header .print-meta { font-size: 9pt; color: #333; }
          .ticket-table {
            box-shadow: none !important;
            border: 1px solid #999;
            width: 100% !important;
            table-layout: fixed;
          }
          /* Repeat the header row at the top of every printed page */
          .ticket-table thead { display: table-header-group; }
          .ticket-table tfoot { display: table-footer-group; }
          /* Plain black text everywhere — nothing depends on the printer
             reproducing colours, and no light greys or coloured chips. */
          .print-header, .print-header *,
          .list-view-wrap, .list-view-wrap * { color: #000 !important; }
          .ticket-table thead tr {
            background: #fff !important;
            color: #000 !important;
            border-bottom: 2px solid #000;
          }
          .ticket-table thead th { font-weight: 700; }
          .ticket-table tr { page-break-inside: avoid; }
          .ticket-table th { padding: 5px 6px; font-size: 11pt; }
          .ticket-table td {
            padding: 5px 6px;
            font-size: 11pt;
            border-bottom: 1px solid #ddd;
            word-wrap: break-word;
            overflow-wrap: anywhere;
          }
          /* Everything prints solid black: on screen several cells are light
             grey, and Status/Priority are white text on coloured chips, which
             printers drop — they came out blank on paper. */
          .ticket-table td, .ticket-table td span, .ticket-table td select { color: #000 !important; }
          .status-badge, .priority-badge {
            background: none !important;
            color: #000 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            font-size: 11pt !important;
            font-weight: 600;
          }
          /* Fixed widths keep the table inside the page, so the browser prints
             at full size instead of shrinking everything to fit. */
          .col-id { width: 7%; }
          .col-type { width: 11%; }
          .col-title { width: 27%; }
          .col-assigned { width: 14%; }
          .col-status { width: 9%; }
          .col-priority { width: 9%; }
          .col-school { width: 15%; }
          .col-date { width: 10%; }
          .ticket-table select { width: 100% !important; min-width: 0 !important; }
          html, body { width: auto !important; min-width: 0 !important; overflow: visible !important; }
          .ticket-title-link { color: #000 !important; border-bottom: none !important; font-weight: 600; }
          .rel-tag {
            background: transparent !important;
            color: #000 !important;
            padding: 0 !important;
            font-size: 9pt;
          }
          /* Dropdowns (e.g. Assigned To) print as plain text */
          .ticket-table select {
            border: none !important;
            background: transparent !important;
            -webkit-appearance: none;
            appearance: none;
            padding: 0 !important;
            font-size: 10pt;
            color: #000;
          }
        }

        @media only screen and (max-width: 360px) {
          .kanban-board { padding: 8px; gap: 10px; }
          .kanban-card strong { font-size: 12px; }
          .kanban-card p { font-size: 10px; }
          .toggle-btn { font-size: 10px; padding: 5px 8px; }
          .filter-bar select, .filter-bar input[type="date"], .filter-bar input[type="text"] {
            flex: 1 1 100%;
          }
          .filter-group { flex: 1 1 100%; }
        }
      `}</style>

      {/* Welcome Bar */}
      <div className="welcome-bar">
        <span className="welcome-text">
          Welcome
          {userName && <span className="welcome-name">{userName.charAt(0).toUpperCase() + userName.slice(1).toLowerCase()}</span>}
          {!userName && "back!"}
        </span>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Ticket Management Portal</span>
      </div>

      <Header
        title="Ticket Management"
        options={(function(){
          var isAdmin = history.location.pathname.indexOf("/tickets/admin/") === 0;
          var assignedUrl = isAdmin
            ? "/tickets/admin/mytickets/" + sid + "/" + uid
            : "/tickets/user/assigned_tickets/" + sid + "/" + uid;
          var creatingUrl = isAdmin
            ? "/tickets/admin/creating/" + sid + "/" + uid
            : "/tickets/user/creating/" + sid + "/" + uid;
          var opts = [
            { title: "Create Ticket", url: creatingUrl },
            { title: isAdmin ? "My Tickets" : "Assigned Tickets", url: assignedUrl },
          ];
          if (isAdmin) {
            opts.push({ title: "Email Alerts", url: "/tickets/admin/email-alerts/" + sid + "/" + uid });
          }
          return opts;
        })()}
        ChangePage={(params) => history.push(params.url)}
      />

      {/* View Toggle */}
      <div className="view-toggle-bar">
        <button
          className={"toggle-btn " + (viewMode === "list" ? "active" : "")}
          onClick={() => { setViewMode("list"); closePanel(); }}
        >
          ☰ All Tickets (List View)
        </button>
        <button
          className={"toggle-btn " + (viewMode === "kanban" ? "active" : "")}
          onClick={() => { setViewMode("kanban"); closePanel(); }}
        >
          ⬜ Ticket Board
        </button>
      </div>

      {/* ── KANBAN VIEW ── */}
      {viewMode === "kanban" && (
        <div className="main-area">
          <div className="board-area">
            <div className="kanban-board">
              {[
                { label: "Open", items: open, color: "#2563eb" },
                //{ label: "In Progress", items: progress, color: "#d97706" },
                { label: "Closed", items: closed, color: "#16a34a" },
              ].map((col) => (
                <div className="kanban-column" key={col.label}>
                  <div className="kanban-col-header">
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: col.color, display: "inline-block", flexShrink: 0 }} />
                    <h3>{col.label}</h3>
                    <span className="col-count">{col.items.length}</span>
                  </div>
                  {col.items.map((d, i) => {
                    var isSelected = selectedTicket && selectedTicket.id === d.id;
                    return (
                      <div
                        key={i}
                        className={"kanban-card" + (isSelected ? " selected" : "")}
                        style={{ borderLeftColor: col.color }}
                        onClick={() => handleCardClick(d)}
                      >
                        <strong>{d.title}</strong>
                        <p>{d.description ? d.description.substring(0, 80) : ""}</p>
                        <div className="card-footer">
                          <span className="card-id">#{d.id}</span>
                          <span style={{ color: priorityColor(d.priority), fontWeight: 600 }}>
                            {d.priority || "—"}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="click-hint">↩ Click again to open full details</div>
                        )}
                      </div>
                    );
                  })}
                  {col.items.length === 0 && (
                    <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 12, padding: "20px 0" }}>
                      No tickets
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right Detail Panel */}
          {selectedTicket && (
            <div className="detail-panel">
              <div className="panel-header">
                <div className="panel-header-left">
                  <div className="panel-ticket-id">#{selectedTicket.id}</div>
                  {/* Title as link → navigates to detail page */}
                  <span
                    className="panel-title-link"
                    onClick={() => history.push(detailUrl(selectedTicket))}
                    title="Open full ticket details"
                  >
                    {selectedTicket.title} ↗
                  </span>
                </div>
                <button className="panel-close" onClick={closePanel} title="Close panel">✕</button>
              </div>

              <div className="panel-body">
                <div className="panel-row">
                  <span className="panel-label">Status</span>
                  <span className="status-badge" style={{ background: statusColor(selectedTicket.status) }}>
                    {selectedTicket.status}
                  </span>
                </div>
                <div className="panel-row">
                  <span className="panel-label">Priority</span>
                  <span className="priority-badge" style={{ background: priorityColor(selectedTicket.priority) }}>
                    {selectedTicket.priority || "—"}
                  </span>
                </div>
                <div className="panel-row">
                  <span className="panel-label">School</span>
                  <span className="panel-value">
                    {selectedTicket.school_name || selectedTicket.schoolName || selectedTicket.school || "—"}
                  </span>
                </div>
                <div className="panel-row">
                  <span className="panel-label">Date</span>
                  <span className="panel-value">
                    {formatDate(selectedTicket.created_at || selectedTicket.createdAt || selectedTicket.date)}
                  </span>
                </div>
                {selectedTicket.assigned_to && (
                  <div className="panel-row">
                    <span className="panel-label">Assigned To</span>
                    <span className="panel-value">{selectedTicket.assigned_to}</span>
                  </div>
                )}
                <hr className="panel-divider" />
                <div className="panel-desc-label">Description</div>
                <div className="panel-desc">
                  {selectedTicket.description || "No description provided."}
                </div>
              </div>

              <span
                className="panel-open-btn"
                onClick={() => history.push(detailUrl(selectedTicket))}
              >
                Open Full Details →
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {viewMode === "list" && (
        <>
          <div className="filter-bar">
            <div className="filter-group">
              <input
                type="text"
                placeholder="Search Ticket ID / Subject / Assigned Staff"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ minWidth: 260 }}
              />
            </div>
            {personalScope && (
              <div className="filter-group">
                <label>Show</label>
                <select value={filterRelation} onChange={(e) => setFilterRelation(e.target.value)}>
                  <option value="All">All my tickets</option>
                  <option value="Assigned to me">Assigned to me</option>
                  <option value="Created by me">Created by me</option>
                </select>
              </div>
            )}
            <div className="filter-group">
              <label>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option>All</option>
                <option>Open</option>
                <option>Closed</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Department</label>
              <select value={filterCommittee} onChange={(e) => setFilterCommittee(e.target.value)}>
                {committeeNames.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Assigned To</label>
              <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
                {assigneeNames.map((a) => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>School</label>
              <select value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)}>
                {schoolNames.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>From</label>
              <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>To</label>
              <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
            </div>
            <button className="reset-btn" onClick={resetFilters}>✕ Reset</button>
            <button className="export-btn" onClick={exportToExcel}>
              ⬇ Export Excel ({sortedData.length})
            </button>
            <button
              className="print-btn"
              onClick={() => printWithFileName("Ticket_Report_" + moment().format("YYYY-MM-DD"))}
            >
              🖨 Print ({sortedData.length})
            </button>
          </div>

          <div className="list-view-wrap">
            {/* Paper-only heading: the filter bar itself is not printed, so the
                printout still says what it is and which filters produced it. */}
            <div className="print-header">
              <h2>Ticket Report</h2>
              <div className="print-meta">
                {userName ? "Printed by " + userName.charAt(0).toUpperCase() + userName.slice(1).toLowerCase() + " • " : ""}
                {moment().format("DD MMM YYYY, hh:mm A")} • {sortedData.length} ticket(s)
                {" • Status: " + filterStatus}
                {filterCommittee !== "All" ? " • Department: " + filterCommittee : ""}
                {filterAssignee !== "All" ? " • Assigned To: " + filterAssignee : ""}
                {filterSchool !== "All" ? " • School: " + filterSchool : ""}
                {filterDateFrom || filterDateTo ? " • Dates: " + (filterDateFrom || "…") + " to " + (filterDateTo || "…") : ""}
                {searchQuery.trim() ? " • Search: " + searchQuery.trim() : ""}
              </div>
            </div>
            <div className="results-count">
              Showing <strong>{sortedData.length}</strong> of <strong>{data.length}</strong> tickets
            </div>
            <table className="ticket-table">
              <thead>
                <tr>
                  <th className="sortable col-id" onClick={() => handleSort("id")}>#ID{sortIndicator("id")}</th>
                  {personalScope && <th className="col-type">Type</th>}
                  <th className="sortable col-title" onClick={() => handleSort("title")}>Title{sortIndicator("title")}</th>
                  <th className="col-desc">Description</th>
                  <th className="sortable col-assigned" onClick={() => handleSort("assigned")}>Assigned To{sortIndicator("assigned")}</th>
                  <th className="sortable col-status" onClick={() => handleSort("status")}>Status{sortIndicator("status")}</th>
                  <th className="sortable col-priority" onClick={() => handleSort("priority")}>Priority{sortIndicator("priority")}</th>
                  <th className="col-school">School</th>
                  <th className="sortable col-date" onClick={() => handleSort("date")}>Date{sortIndicator("date")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={personalScope ? 9 : 8}>
                      <div className="empty-state">No tickets match the selected filters.</div>
                    </td>
                  </tr>
                ) : (
                  sortedData.map((d, i) => (
                    <tr key={i}>
                      <td className="col-id" style={{ color: "#94a3b8", fontWeight: 600 }}>#{d.id}</td>
                      {personalScope && (
                        <td className="col-type">
                          <span className={"rel-tag rel-" + relationOf(d).split(" ")[0].toLowerCase()}>
                            {relationOf(d)}
                          </span>
                        </td>
                      )}
                      <td className="col-title">
                        <span
                          className="ticket-title-link"
                          onClick={() => history.push(detailUrl(d))}
                        >
                          {d.title}
                        </span>
                      </td>
                      <td className="col-desc" style={{ color: "#6b7280", maxWidth: 240 }}>
                        {d.description
                          ? d.description.substring(0, 100) + (d.description.length > 100 ? "…" : "")
                          : "—"}
                      </td>
                      <td className="col-assigned">
                        {canManageTicket(d) ? (
                          <>
                            <select
                              value={d.staff_id || ""}
                              disabled={assignUpdating === d.id}
                              onChange={(e) => {
                                var selectedId = e.target.value;
                                var staffObj = null;
                                if (selectedId) {
                                  Object.keys(staffDetails).some((type) => {
                                    var found = (staffDetails[type] || []).find(
                                      (s) => String(s.staff_id) === String(selectedId)
                                    );
                                    if (found) { staffObj = found; return true; }
                                    return false;
                                  });
                                }
                                assignTicket(d, staffObj);
                              }}
                              style={{ padding: "4px 6px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, color: "#374151", background: "#f9fafb" }}
                            >
                              <option value="">Unassigned</option>
                              {Object.keys(staffDetails).map((type) => (
                                <optgroup key={type} label={type}>
                                  {filterGenericStaff(staffDetails[type] || []).map((s) => (
                                    <option key={s.staff_id} value={s.staff_id}>
                                      {s.first_name} {s.last_name || ""}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                            {assignUpdating === d.id && (
                              <span style={{ marginLeft: 6, fontSize: 11, color: "#6b7280" }}>Saving…</span>
                            )}
                          </>
                        ) : (
                          d.staff || d.assigned_to || "Unassigned"
                        )}
                      </td>
                      <td className="col-status">
                        <span className="status-badge" style={{ background: statusColor(d.status) }}>
                          {d.status}
                        </span>
                      </td>
                      <td className="col-priority">
                        <span className="priority-badge" style={{ background: priorityColor(d.priority) }}>
                          {d.priority || "—"}
                        </span>
                      </td>
                      <td className="col-school">{d.school_name || d.schoolName || d.school || "—"}</td>
                      <td className="col-date" style={{ color: "#94a3b8", whiteSpace: "nowrap" }}>
                        {formatDate(d.created_at || d.createdAt || d.date)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
